/**
 * Room protocol client: one WebSocket, bounded-backoff reconnect, command
 * correlation, and clock sampling.
 *
 * Structure follows plan/03 item 5: there is exactly one way for user intent
 * to reach the room (`send`), and the receive path never produces commands.
 * The client therefore has no opinion about room state — it forwards
 * snapshots verbatim and lets the state layer apply them.
 *
 * Reconnect rules (plan/03 item 11): no command is queued while the socket is
 * down, pending commands fail immediately on loss instead of being
 * retransmitted into a surprise execution, clock samples are discarded, and
 * the next snapshot is the only source of truth. A close whose cause is an
 * invalid session is terminal — retrying a dead cookie forever would only hide
 * the real remedy (a fresh invite exchange), so the session is probed once per
 * loss to tell the two cases apart.
 *
 * DOM/browser surface used: `WebSocket`, `performance.now()`, `Date.now()`
 * (suspend detection only), `crypto.randomUUID()`, and `location`.
 */

import type { ChatMessage, ClientMessage, CommandResult, RoomSnapshot } from '../generated';
import { ApiError, fetchSession } from '../session/api';
import { CLOCK_TUNING, ClockEstimator, SuspendDetector, type ClockEstimate } from '../player/clock';
import {
  decodeServerMessage,
  encodeClientMessage,
  type CommandIntent,
  type PlaybackReportMessage,
  type ReactionEvent,
} from './protocol';

export type ConnectionPhase =
  /** Not connected yet. */
  | 'connecting'
  /** Snapshot received; commands allowed. */
  | 'open'
  /** Lost the socket, retrying with backoff. */
  | 'reconnecting'
  /** The server no longer accepts this session; a fresh invite exchange is required. */
  | 'session-lost'
  /** Terminal protocol failure (e.g. version mismatch); reload required. */
  | 'protocol-error'
  /** `dispose()` was called. */
  | 'closed';

export interface ConnectionStatus {
  readonly phase: ConnectionPhase;
  /** Consecutive failed connection attempts. */
  readonly attempt: number;
  /** Milliseconds until the next attempt, when reconnecting. */
  readonly nextRetryInMs: number | null;
  /** User-facing detail for the non-healthy phases. */
  readonly message: string | null;
}

/** `CommandResult` plus the local outcomes that never reached the room. */
export type CommandOutcome =
  | CommandResult
  | { readonly result: 'failed'; readonly reason: string; readonly message: string };

export interface RoomClientHandlers {
  /** A full snapshot arrived. `receivedAtMs` is client monotonic time. */
  onSnapshot: (snapshot: RoomSnapshot, receivedAtMs: number) => void;
  onStatus: (status: ConnectionStatus) => void;
  /** Clock estimate changed (new sample, or reset to `null`). */
  onClock: (estimate: ClockEstimate | null) => void;
  /**
   * A chat message was broadcast (including one this client just sent —
   * there is no optimistic local echo).
   */
  onChat: (message: ChatMessage) => void;
  /** An ephemeral reaction was broadcast. Not replayed after a reconnect. */
  onReaction: (reaction: ReactionEvent) => void;
  /** Connection-level `ServerMessage::Error`; the socket closes afterwards. */
  onServerError: (reason: string, message: string) => void;
}

/** Backoff schedule for reconnect attempts, in ms; the last value repeats. */
const BACKOFF_MS: readonly number[] = [500, 1000, 2000, 4000, 8000, 15_000];
/** ±20 % jitter so several clients do not retry in lockstep. */
const BACKOFF_JITTER = 0.2;
/** A command with no ack within this window is reported as failed. */
const COMMAND_TIMEOUT_MS = 10_000;

interface PendingCommand {
  readonly settle: (outcome: CommandOutcome) => void;
  /** DOM `setTimeout` handle. */
  readonly timer: number;
}

export class RoomClient {
  readonly #handlers: RoomClientHandlers;
  readonly #clock = new ClockEstimator();
  readonly #pending = new Map<string, PendingCommand>();
  #suspend = new SuspendDetector(performance.now(), Date.now());

  #socket: WebSocket | null = null;
  #status: ConnectionStatus = {
    phase: 'connecting',
    attempt: 0,
    nextRetryInMs: null,
    message: null,
  };
  #attempt = 0;
  #reconnectTimer: number | undefined;
  #pingTimer: number | undefined;
  #epoch: bigint | null = null;
  #disposed = false;

  constructor(handlers: RoomClientHandlers) {
    this.#handlers = handlers;
  }

  get status(): ConnectionStatus {
    return this.#status;
  }

  /** Estimated server clock for "now", or `null` before the first estimate. */
  serverNowMs(): number | null {
    return this.#clock.serverNowMs(performance.now());
  }

  clockEstimate(): ClockEstimate | null {
    return this.#clock.estimate(performance.now());
  }

  connect(): void {
    if (this.#disposed || this.#socket !== null) return;
    this.#setStatus({
      phase: this.#attempt === 0 ? 'connecting' : 'reconnecting',
      attempt: this.#attempt,
      nextRetryInMs: null,
      message: null,
    });

    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Same-origin URL: the browser attaches the HttpOnly session cookie and
    // sends an `Origin` the server can validate. There is no token in the URL.
    const socket = new WebSocket(`${scheme}//${window.location.host}/ws`);
    this.#socket = socket;

    socket.onopen = () => {
      // The socket being open is not yet "joined": the server's snapshot is.
      // Clock sampling starts immediately because the snapshot's timestamps
      // are already useful.
      this.#clock.reset();
      this.#handlers.onClock(null);
      this.#suspend = new SuspendDetector(performance.now(), Date.now());
      this.#schedulePing(0);
    };

    socket.onmessage = (event: MessageEvent<unknown>) => {
      if (typeof event.data !== 'string') return;
      this.#receive(event.data, performance.now());
    };

    socket.onerror = () => {
      // `close` always follows; the error event carries no usable detail.
    };

    socket.onclose = (event: CloseEvent) => {
      if (this.#socket !== socket) return;
      this.#teardownSocket();
      if (this.#disposed) return;
      if (this.#status.phase === 'protocol-error' || this.#status.phase === 'session-lost') return;
      this.#failPending('disconnected', 'The connection to the room dropped before the server answered.');
      void this.#scheduleReconnect(event.code);
    };
  }

  /** Stops everything; the instance is not reusable afterwards. */
  dispose(): void {
    this.#disposed = true;
    this.#teardownSocket();
    clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = undefined;
    this.#failPending('closed', 'The page stopped talking to the room.');
    this.#setStatus({ phase: 'closed', attempt: 0, nextRetryInMs: null, message: null });
  }

  /**
   * Sends one command and resolves with its outcome. Never throws: a refusal,
   * a dead socket, and a timeout are all outcomes the caller renders.
   */
  send(intent: CommandIntent): Promise<CommandOutcome> {
    const socket = this.#socket;
    const epoch = this.#epoch;
    if (socket === null || socket.readyState !== WebSocket.OPEN || epoch === null) {
      return Promise.resolve({
        result: 'failed',
        reason: 'disconnected',
        message: 'Not connected to the room right now — nothing was sent.',
      });
    }

    const commandId = crypto.randomUUID();
    const message = { ...intent, command_id: commandId, epoch } as ClientMessage;
    const { promise, resolve } = Promise.withResolvers<CommandOutcome>();
    const timer = setTimeout(() => {
      this.#pending.delete(commandId);
      resolve({
        result: 'failed',
        reason: 'timeout',
        message: 'The server did not answer that action. Its current state is shown above.',
      });
    }, COMMAND_TIMEOUT_MS);
    this.#pending.set(commandId, {
      timer,
      settle: (outcome) => {
        clearTimeout(timer);
        resolve(outcome);
      },
    });

    try {
      socket.send(encodeClientMessage(message));
    } catch {
      this.#pending.delete(commandId);
      clearTimeout(timer);
      return Promise.resolve({
        result: 'failed',
        reason: 'send-failed',
        message: 'The browser could not send that action. Check the connection status.',
      });
    }
    return promise;
  }

  /**
   * Fire-and-forget telemetry. Unacknowledged by design, and dropped silently
   * while disconnected — a report is only a hint about this connection.
   */
  sendPlaybackReport(message: PlaybackReportMessage): void {
    const socket = this.#socket;
    if (socket === null || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(encodeClientMessage(message));
    } catch {
      // The close handler deals with a broken socket.
    }
  }

  #receive(data: string, receivedAtMs: number): void {
    const decoded = decodeServerMessage(data);
    if (!decoded.ok) {
      if (decoded.reason === 'protocol-version-mismatch') {
        this.#setStatus({
          phase: 'protocol-error',
          attempt: 0,
          nextRetryInMs: null,
          message: decoded.message,
        });
        this.#socket?.close();
        this.#teardownSocket();
      }
      return;
    }

    const message = decoded.message;
    switch (message.type) {
      case 'snapshot': {
        const { type: _type, ...snapshot } = message;
        this.#epoch = snapshot.epoch;
        this.#clock.bootstrap(snapshot.server_now_ms, receivedAtMs);
        this.#attempt = 0;
        this.#setStatus({ phase: 'open', attempt: 0, nextRetryInMs: null, message: null });
        this.#handlers.onSnapshot(snapshot, receivedAtMs);
        this.#handlers.onClock(this.#clock.estimate(receivedAtMs));
        return;
      }
      case 'command-ack': {
        const pending = this.#pending.get(message.command_id);
        if (pending === undefined) return;
        this.#pending.delete(message.command_id);
        pending.settle(message.outcome);
        return;
      }
      case 'clock-pong': {
        const sample = this.#clock.acceptPong(
          message.client_send_ms,
          message.server_receive_ms,
          message.server_send_ms,
          receivedAtMs,
        );
        if (sample !== null) this.#handlers.onClock(this.#clock.estimate(receivedAtMs));
        return;
      }
      case 'chat':
        this.#handlers.onChat(message.message);
        return;
      case 'reaction': {
        const { type: _type, ...reaction } = message;
        this.#handlers.onReaction(reaction);
        return;
      }
      case 'error':
        this.#handlers.onServerError(message.reason, message.message);
        return;
    }
  }

  #schedulePing(delayMs: number): void {
    clearTimeout(this.#pingTimer);
    this.#pingTimer = setTimeout(() => {
      const socket = this.#socket;
      if (socket === null || socket.readyState !== WebSocket.OPEN) return;
      const now = performance.now();
      // A suspend/resume invalidates every retained sample: the machine's
      // monotonic clock stopped while the server's kept running.
      if (this.#suspend.check(now, Date.now())) {
        this.#clock.reset();
        this.#handlers.onClock(null);
      }
      socket.send(
        encodeClientMessage({ type: 'clock-ping', client_send_ms: this.#clock.startPing(now) }),
      );
      this.#schedulePing(this.#clock.nextPingDelayMs());
    }, delayMs);
  }

  async #scheduleReconnect(closeCode: number): Promise<void> {
    this.#attempt += 1;
    const attempt = this.#attempt;
    const base = BACKOFF_MS[Math.min(attempt - 1, BACKOFF_MS.length - 1)] ?? 15_000;
    const delay = Math.round(base * (1 + (Math.random() * 2 - 1) * BACKOFF_JITTER));
    this.#setStatus({
      phase: 'reconnecting',
      attempt,
      nextRetryInMs: delay,
      message: closeCode === 1000 ? 'The server closed the connection.' : null,
    });

    // A 401 on the upgrade surfaces as an ordinary abnormal close, so the only
    // way to tell "cookie expired / room reset" from "network blip" is to ask.
    try {
      await fetchSession();
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized) {
        this.#setStatus({
          phase: 'session-lost',
          attempt,
          nextRetryInMs: null,
          message: error.message,
        });
        return;
      }
      // Unreachable server: that is exactly what the backoff is for.
    }
    if (this.#disposed || this.#status.phase === 'session-lost') return;

    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = undefined;
      this.connect();
    }, delay);
  }

  #teardownSocket(): void {
    const socket = this.#socket;
    this.#socket = null;
    clearTimeout(this.#pingTimer);
    this.#pingTimer = undefined;
    this.#clock.reset();
    this.#handlers.onClock(null);
    this.#epoch = null;
    if (socket !== null) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    }
  }

  #failPending(reason: string, message: string): void {
    for (const pending of this.#pending.values()) {
      clearTimeout(pending.timer);
      pending.settle({ result: 'failed', reason, message });
    }
    this.#pending.clear();
  }

  #setStatus(status: ConnectionStatus): void {
    this.#status = status;
    this.#handlers.onStatus(status);
  }
}

/** Human text for a connection phase, used by the status banner. */
export function connectionHeadline(status: ConnectionStatus): string {
  switch (status.phase) {
    case 'connecting':
      return 'Connecting to the room…';
    case 'open':
      return 'Connected';
    case 'reconnecting':
      return status.attempt <= 1
        ? 'Connection lost — reconnecting…'
        : `Connection lost — reconnecting (attempt ${status.attempt})…`;
    case 'session-lost':
      return 'Session expired';
    case 'protocol-error':
      return 'Incompatible server version';
    case 'closed':
      return 'Disconnected';
  }
}

export { CLOCK_TUNING };
